import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import Modal from 'tio-ui/components/modal';
import Button from 'tio-ui/components/button';
import { not } from 'tio-ui/utilities';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type {
  DemoClientId,
  DemoProductId,
  DemoSessionRoleId,
  DemoTaPaymentModelId,
  DemoDebtLevelId,
  DemoUserScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

interface DemoDataShortcutSignature {
  Args: Record<string, never>;
}

export default class DemoDataShortcut extends Component<DemoDataShortcutSignature> {
  @service declare demoMode: DemoModeService;
  @service declare router: RouterService;

  @tracked isOpen = false;

  /**
   * Bumped on every route transition (see trackRouteChanges) so the pill
   * getters recompute: demo activation is PATH-scoped but read from
   * `window.location`, which Ember does not track.
   */
  @tracked routeRevision = 0;
  @tracked selectedClientId: DemoClientId;
  @tracked selectedProductIds: DemoProductId[] = [];
  @tracked selectedRoleId: DemoSessionRoleId;
  @tracked selectedUserScenarioId: DemoUserScenarioId;
  @tracked selectedTaPaymentModelId: DemoTaPaymentModelId;
  @tracked selectedDebtLevelId: DemoDebtLevelId;

  constructor(owner: Owner, args: DemoDataShortcutSignature['Args']) {
    super(owner, args);

    const selection = this.demoMode.currentSelection;
    this.selectedClientId = selection.clientId;
    this.selectedProductIds = [...selection.productIds];
    this.selectedRoleId = selection.roleId;
    this.selectedUserScenarioId = selection.userScenarioId;
    this.selectedTaPaymentModelId = selection.taPaymentModelId;
    this.selectedDebtLevelId = selection.debtLevelId;
  }

  /**
   * Installs the global Ctrl+Shift+~ listener for the lifetime of the host
   * element. Lives in a modifier (not the constructor) per repo convention and
   * is only ever rendered when demo mode is available, so the listener never
   * attaches in a session that cannot use it.
   */
  installShortcut = modifier(() => {
    const handler = (event: KeyboardEvent) => {
      if (!this.isDemoShortcut(event) || this.isEditableTarget(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.openModal();
    };

    document.addEventListener('keydown', handler);

    return () => document.removeEventListener('keydown', handler);
  });

  /**
   * Invalidates the pill state on every route transition for the lifetime of
   * the host element (the demo-active/paused split depends on the current
   * path — see routeRevision).
   */
  trackRouteChanges = modifier(() => {
    const bump = () => {
      this.routeRevision++;
    };
    this.router.on('routeDidChange', bump);

    return () => this.router.off('routeDidChange', bump);
  });

  /**
   * Whether the applied demo session renders on the current route — drives the
   * active demo pill. `routeRevision >= 0` is always true; reading it makes
   * this getter recompute per transition.
   */
  get isDemoActive() {
    return this.routeRevision >= 0 && this.demoMode.isActive;
  }

  /**
   * A sticky demo session is armed but the current route is not demoable, so
   * the page is showing REAL data — drives the paused-pill warning state.
   */
  get isDemoPaused() {
    return this.routeRevision >= 0 && !this.demoMode.isActive && this.demoMode.isArmed;
  }

  /** There is a demo session to exit (active here or armed elsewhere). */
  get canExitDemo() {
    return this.isDemoActive || this.isDemoPaused;
  }

  /**
   * The APPLIED session's persona for the pill label — unlike
   * selectedClientName, which tracks the modal's in-flight (unapplied)
   * selection.
   */
  get activePersonaName() {
    return this.demoMode.clientName(this.demoMode.currentSelection.clientId);
  }

  exitDemo = () => {
    this.demoMode.exit();
  };

  resumeDemo = () => {
    this.demoMode.resume();
  };

  get clients() {
    return this.demoMode.clients;
  }

  get products() {
    return this.demoMode.products;
  }

  get roles() {
    return this.demoMode.roles;
  }

  get userScenarios() {
    return this.demoMode.userScenarios;
  }

  get taPaymentModels() {
    return this.demoMode.taPaymentModels;
  }

  get debtLevels() {
    return this.demoMode.debtLevels;
  }

  get selectedClientName() {
    return this.demoMode.clientName(this.selectedClientId);
  }

  get selectedProductNames() {
    return this.selectedProductIds.map((id) => this.demoMode.productName(id)).join(', ');
  }

  get selectedRoleName() {
    return this.demoMode.roleName(this.selectedRoleId);
  }

  get selectedUserScenarioName() {
    return this.demoMode.userScenarioName(this.selectedUserScenarioId);
  }

  get selectedTaPaymentModelName() {
    return this.demoMode.taPaymentModelName(this.selectedTaPaymentModelId);
  }

  get selectedDebtLevelName() {
    return this.demoMode.debtLevelName(this.selectedDebtLevelId);
  }

  // Internal-only derived state (used by isEmployeeExperience, not the template).
  private get selectedRole() {
    return this.roles.find((role) => role.id === this.selectedRoleId);
  }

  /**
   * Whether the selected role simulates the employee/participant experience.
   * The "user scenario" cards are employee-journey personas (First Login,
   * New Employee, Power User, Benefits Maxed, …), so they only make sense for
   * the employee. The Supervisor (approver) and Admin (partner-admin) views have
   * no employee benefits journey to pick — forcing a scenario choice there is
   * incoherent, so the section is hidden for those roles.
   */
  get isEmployeeExperience() {
    return this.selectedRole?.relationshipType === 'PARTICIPANT';
  }

  /**
   * Whether Tuition Assistance is among the selected products. The "TA payment
   * model" cards configure TA-only behavior (the TAS assistance manner and the
   * direct-pay/card-review settings), which has no surface unless TA is selected
   * — so the section is hidden otherwise, mirroring the employee-only
   * user-scenario section above. The apply path coerces the persisted model back
   * to the default when TA is not selected, so a stale value never rides along.
   */
  get isTaProduct() {
    return this.demoMode.isTaSelected(this.selectedProductIds);
  }

  /** A product is included in the multi-select set. */
  isProductSelected = (productId: DemoProductId) => {
    return this.selectedProductIds.includes(productId);
  };

  /**
   * Whether the selected persona's employer is PSLF-eligible (non-profit or
   * government). For-profit personas can't qualify for PSLF.
   */
  get isPslfAvailable() {
    return this.demoMode.isPslfEligibleClient(this.selectedClientId);
  }

  /**
   * Products the selected persona makes unavailable — the responsive guard. PSLF
   * is disabled for a for-profit persona (it can never qualify), so the option
   * greys out instead of silently producing an impossible combination.
   */
  isProductDisabled = (productId: DemoProductId) => {
    return productId === 'pslf' && !this.isPslfAvailable;
  };

  /** At least one benefit must be selected to apply a coherent demo session. */
  get canApply() {
    return this.selectedProductIds.length > 0;
  }

  private isDemoShortcut(event: KeyboardEvent) {
    return event.ctrlKey && event.shiftKey && (event.key === '~' || event.code === 'Backquote');
  }

  private isEditableTarget(target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target.isContentEditable ||
      !!target.closest('[contenteditable=""], [contenteditable="true"]')
    );
  }

  openModal = () => {
    const selection = this.demoMode.currentSelection;
    this.selectedClientId = selection.clientId;
    this.selectedProductIds = [...selection.productIds];
    this.selectedRoleId = selection.roleId;
    this.selectedUserScenarioId = selection.userScenarioId;
    this.selectedTaPaymentModelId = selection.taPaymentModelId;
    this.selectedDebtLevelId = selection.debtLevelId;
    this.isOpen = true;
  };

  closeModal = () => {
    this.isOpen = false;
  };

  selectClient = (clientId: DemoClientId) => {
    this.selectedClientId = clientId;
    // Switching to a for-profit persona makes PSLF unavailable — drop it so the
    // applied set matches what the picker now allows (the option also greys out).
    if (!this.demoMode.isPslfEligibleClient(clientId)) {
      this.selectedProductIds = this.selectedProductIds.filter((id) => id !== 'pslf');
    }
  };

  selectProduct = (productId: DemoProductId) => {
    if (this.isProductDisabled(productId)) {
      return;
    }
    // Toggle membership — products are a multi-select set.
    this.selectedProductIds = this.selectedProductIds.includes(productId)
      ? this.selectedProductIds.filter((id) => id !== productId)
      : [...this.selectedProductIds, productId];
  };

  selectRole = (roleId: DemoSessionRoleId) => {
    this.selectedRoleId = roleId;
  };

  selectUserScenario = (userScenarioId: DemoUserScenarioId) => {
    this.selectedUserScenarioId = userScenarioId;
  };

  selectTaPaymentModel = (taPaymentModelId: DemoTaPaymentModelId) => {
    this.selectedTaPaymentModelId = taPaymentModelId;
  };

  selectDebtLevel = (debtLevelId: DemoDebtLevelId) => {
    this.selectedDebtLevelId = debtLevelId;
  };

  apply = () => {
    if (!this.canApply) {
      return;
    }
    this.demoMode.apply({
      clientId: this.selectedClientId,
      productIds: this.selectedProductIds,
      roleId: this.selectedRoleId,
      userScenarioId: this.selectedUserScenarioId,
      taPaymentModelId: this.selectedTaPaymentModelId,
      debtLevelId: this.selectedDebtLevelId,
    });
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{#if this.demoMode.isAvailable}}
      <div hidden {{this.installShortcut}} {{this.trackRouteChanges}}></div>

      {{#if this.isDemoActive}}
        {{! Always-visible demo indicator: the rep can see they're in demo
            mode on EVERY demoable route (reimagined AND admin), jump to the
            picker, or exit in one click. }}
        <div class="tio-demo-pill" data-test-demo-pill>
          <button
            type="button"
            class="tio-demo-pill-main"
            title="Demo settings (Ctrl+Shift+~)"
            data-test-demo-pill-settings
            {{on "click" this.openModal}}
          >
            <span class="tio-demo-pill-dot" aria-hidden="true"></span>
            Demo &middot;
            {{this.activePersonaName}}
          </button>
          <button
            type="button"
            class="tio-demo-pill-exit"
            aria-label="Exit demo mode"
            title="Exit demo mode"
            data-test-demo-pill-exit
            {{on "click" this.exitDemo}}
          >
            &times;
          </button>
        </div>
      {{else if this.isDemoPaused}}
        {{! Armed session on a non-demoable route: the page is showing REAL
            data. Warn instead of disappearing silently. }}
        <div class="tio-demo-pill tio-demo-pill--paused" data-test-demo-pill-paused>
          <span class="tio-demo-pill-label">Demo paused &mdash; showing real data</span>
          <button
            type="button"
            class="tio-demo-pill-action"
            data-test-demo-pill-resume
            {{on "click" this.resumeDemo}}
          >
            Return to demo
          </button>
          <button
            type="button"
            class="tio-demo-pill-exit"
            aria-label="Exit demo mode"
            title="Exit demo mode"
            data-test-demo-pill-exit
            {{on "click" this.exitDemo}}
          >
            &times;
          </button>
        </div>
      {{/if}}

      <Modal
        @isOpen={{this.isOpen}}
        @onClose={{this.closeModal}}
        @size="2xl"
        @ariaLabel="Demo data options"
        data-test-demo-data-modal
      >
        <:header>
          <div>
            <h2 class="tio-text-lg-semibold tio-fg-primary">Demo data</h2>
            <p class="mt-1 text-sm tio-fg-muted">
              Choose the persona, products, and role to apply to this demo session.
            </p>
          </div>
        </:header>

        <:body>
          <div class="space-y-8">
            <section>
              <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Persona</h3>
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {{#each this.clients as |client|}}
                  <button
                    type="button"
                    class="rounded-xl border p-4 text-left transition
                      {{if
                        (eq client.id this.selectedClientId)
                        'border-violet-500 bg-violet-50 ring-2 ring-violet-100'
                        'border-gray-200 hover:border-violet-300 hover:bg-violet-50/50'
                      }}"
                    aria-pressed={{if (eq client.id this.selectedClientId) "true" "false"}}
                    data-test-demo-client={{client.id}}
                    {{on "click" (fn this.selectClient client.id)}}
                  >
                    <div class="font-semibold tio-fg-primary">
                      {{if client.personaLabel client.personaLabel client.industry}}
                    </div>
                    <div class="mt-1 text-sm tio-fg-muted">{{client.name}}</div>
                    <div class="mt-2 text-xs tio-fg-disabled">
                      {{client.employeeCount}}
                      employees &middot;
                      {{client.taUtilization}}% TA utilization
                    </div>
                  </button>
                {{/each}}
              </div>
            </section>

            <section>
              <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                Products
              </h3>
              <p class="mt-1 text-xs tio-fg-disabled">Select any combination of benefits.</p>
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {{#each this.products as |product|}}
                  <button
                    type="button"
                    disabled={{this.isProductDisabled product.id}}
                    class="rounded-xl border p-4 text-left transition
                      {{if
                        (this.isProductDisabled product.id)
                        'tio-border-default opacity-50 cursor-not-allowed'
                        (if
                          (this.isProductSelected product.id)
                          'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100'
                          'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                        )
                      }}"
                    role="checkbox"
                    aria-checked={{if (this.isProductSelected product.id) "true" "false"}}
                    aria-disabled={{if (this.isProductDisabled product.id) "true" "false"}}
                    data-test-demo-product={{product.id}}
                    {{on "click" (fn this.selectProduct product.id)}}
                  >
                    <div class="font-semibold tio-fg-primary">{{product.name}}</div>
                    <div class="mt-1 text-sm tio-fg-muted">{{product.description}}</div>
                    {{#if (this.isProductDisabled product.id)}}
                      <div class="mt-1 text-xs tio-fg-disabled">
                        Requires a non-profit or government employer
                      </div>
                    {{/if}}
                  </button>
                {{/each}}
              </div>
            </section>

            <section>
              <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Role</h3>
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {{#each this.roles as |role|}}
                  <button
                    type="button"
                    class="rounded-xl border p-4 text-left transition
                      {{if
                        (eq role.id this.selectedRoleId)
                        'border-sky-500 bg-sky-50 ring-2 ring-sky-100'
                        'border-gray-200 hover:border-sky-300 hover:bg-sky-50/50'
                      }}"
                    aria-pressed={{if (eq role.id this.selectedRoleId) "true" "false"}}
                    data-test-demo-role={{role.id}}
                    {{on "click" (fn this.selectRole role.id)}}
                  >
                    <div class="font-semibold tio-fg-primary">{{role.name}}</div>
                    <div class="mt-1 text-sm tio-fg-muted">{{role.description}}</div>
                  </button>
                {{/each}}
              </div>
            </section>

            {{#if this.isEmployeeExperience}}
              <section>
                <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                  User scenario
                </h3>
                <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {{#each this.userScenarios as |scenario|}}
                    <button
                      type="button"
                      class="rounded-xl border p-4 text-left transition
                        {{if
                          (eq scenario.id this.selectedUserScenarioId)
                          'border-amber-500 bg-amber-50 ring-2 ring-amber-100'
                          'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                        }}"
                      aria-pressed={{if
                        (eq scenario.id this.selectedUserScenarioId)
                        "true"
                        "false"
                      }}
                      data-test-demo-user-scenario={{scenario.id}}
                      {{on "click" (fn this.selectUserScenario scenario.id)}}
                    >
                      <div class="font-semibold tio-fg-primary">{{scenario.name}}</div>
                      <div class="mt-1 text-sm tio-fg-muted">{{scenario.description}}</div>
                    </button>
                  {{/each}}
                </div>
              </section>
            {{/if}}

            {{#if this.isTaProduct}}
              <section>
                <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                  TA payment model
                </h3>
                <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {{#each this.taPaymentModels as |model|}}
                    <button
                      type="button"
                      class="rounded-xl border p-4 text-left transition
                        {{if
                          (eq model.id this.selectedTaPaymentModelId)
                          'border-rose-500 bg-rose-50 ring-2 ring-rose-100'
                          'border-gray-200 hover:border-rose-300 hover:bg-rose-50/50'
                        }}"
                      aria-pressed={{if (eq model.id this.selectedTaPaymentModelId) "true" "false"}}
                      data-test-demo-ta-payment-model={{model.id}}
                      {{on "click" (fn this.selectTaPaymentModel model.id)}}
                    >
                      <div class="font-semibold tio-fg-primary">{{model.name}}</div>
                      <div class="mt-1 text-sm tio-fg-muted">{{model.description}}</div>
                    </button>
                  {{/each}}
                </div>
              </section>
            {{/if}}

            <section>
              <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Debt</h3>
              <p class="mt-1 text-xs tio-fg-disabled">
                Sets the employee's linked-loan balance across the loan, SLR, and IDR surfaces.
              </p>
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {{#each this.debtLevels as |level|}}
                  <button
                    type="button"
                    class="rounded-xl border p-4 text-left transition
                      {{if
                        (eq level.id this.selectedDebtLevelId)
                        'tio-border-brand tio-bg-brand-soft'
                        'tio-border-default hover:tio-border-brand-soft hover:tio-bg-brand-soft-hover'
                      }}"
                    aria-pressed={{if (eq level.id this.selectedDebtLevelId) "true" "false"}}
                    data-test-demo-debt-level={{level.id}}
                    {{on "click" (fn this.selectDebtLevel level.id)}}
                  >
                    <div class="font-semibold tio-fg-primary">{{level.name}}</div>
                    <div class="mt-1 text-sm tio-fg-muted">{{level.description}}</div>
                  </button>
                {{/each}}
              </div>
            </section>
          </div>
        </:body>

        <:footer>
          <div class="tio-col sm:flex-row sm:items-center sm:justify-between">
            <p class="text-sm tio-fg-muted">
              Applying:
              <span class="font-medium tio-fg-secondary">{{this.selectedClientName}}</span>
              /
              <span class="font-medium tio-fg-secondary">{{this.selectedProductNames}}</span>
              /
              <span class="font-medium tio-fg-secondary">{{this.selectedRoleName}}</span>
              {{#if this.isEmployeeExperience}}
                /
                <span class="font-medium tio-fg-secondary">{{this.selectedUserScenarioName}}</span>
              {{/if}}
              {{#if this.isTaProduct}}
                /
                <span
                  class="font-medium tio-fg-secondary"
                >{{this.selectedTaPaymentModelName}}</span>
              {{/if}}
              /
              <span class="font-medium tio-fg-secondary">{{this.selectedDebtLevelName}}</span>
            </p>
            <div class="tio-flex-end gap-3">
              {{#if this.canExitDemo}}
                <Button
                  @appearance="outlined"
                  @intent="danger"
                  data-test-demo-exit-session
                  {{on "click" this.exitDemo}}
                >
                  Exit demo mode
                </Button>
              {{/if}}
              <Button @appearance="outlined" @intent="default" {{on "click" this.closeModal}}>
                Cancel
              </Button>
              <Button
                @intent="primary"
                @isDisabled={{not this.canApply}}
                data-test-demo-apply
                {{on "click" this.apply}}
              >
                Apply demo data
              </Button>
            </div>
          </div>
        </:footer>
      </Modal>
    {{/if}}
  </template>
}
